import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AI Providers Page Component
 *
 * Configure AI provider settings with inline styles for WordPress admin compatibility
 *
 * @package WooAIChatbot
 */
import { useState, useEffect } from 'react';
import { Eye, EyeOff, Play, Save, AlertCircle, CheckCircle, Plus, Trash2, GripVertical } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import { useTranslation } from '../../hooks/useTranslation';
import { AI_MODELS, MODEL_LABELS } from '../../types/settings';
// Inline styles
const styles = {
    page: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        maxWidth: '832px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    link: {
        color: '#6366f1',
        textDecoration: 'underline',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: 400,
        color: '#111827',
        margin: '0 0 40px 0',
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    select: {
        width: '100%',
        height: '40px',
        padding: '0 12px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        backgroundColor: '#ffffff',
        color: '#111827',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236b7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundSize: '16px',
        paddingRight: '40px',
    },
    input: {
        width: '100%',
        height: '36px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        backgroundColor: '#f3f3f5',
        color: '#111827',
        boxSizing: 'border-box',
    },
    inputWithButton: {
        width: '100%',
        height: '36px',
        padding: '0 40px 0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        backgroundColor: '#f3f3f5',
        color: '#111827',
        boxSizing: 'border-box',
    },
    inputRow: {
        display: 'flex',
        gap: '8px',
    },
    inputWrapper: {
        flex: 1,
        width: '100%',
        position: 'relative',
    },
    eyeButton: {
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        padding: '8px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '4px',
    },
    testButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    testButtonDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#f9fafb',
        color: '#9ca3af',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
        opacity: 0.5,
    },
    hint: {
        fontSize: '12px',
        color: '#9ca3af',
        margin: 0,
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        paddingTop: '4px',
    },
    checkbox: {
        width: '16px',
        height: '16px',
        cursor: 'pointer',
        accentColor: '#6366f1',
    },
    checkboxLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
        cursor: 'pointer',
    },
    sliderContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    sliderHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    sliderValue: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
    },
    slider: {
        width: '100%',
        height: '8px',
        borderRadius: '4px',
        cursor: 'pointer',
        accentColor: '#6366f1',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '12px',
        paddingTop: '4px',
        borderTop: '1px solid #e5e7eb',
    },
    cancelButton: {
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    saveButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    saveButtonDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
        opacity: 0.5,
    },
    alert: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        borderRadius: '8px',
    },
    alertError: {
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
    },
    alertSuccess: {
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
    },
    alertIcon: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
        marginTop: '2px',
    },
    alertContent: {
        flex: 1,
    },
    alertTitle: {
        fontWeight: 500,
        margin: 0,
    },
    alertMessage: {
        fontSize: '14px',
        marginTop: '4px',
    },
    testResult: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        marginTop: '4px',
    },
    loading: {
        padding: '32px',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '256px',
    },
    loadingContent: {
        textAlign: 'center',
    },
    spinner: {
        width: '48px',
        height: '48px',
        border: '2px solid transparent',
        borderBottomColor: '#6366f1',
        borderRadius: '50%',
        margin: '0 auto 16px',
        animation: 'spin 1s linear infinite',
    },
    smallSpinner: {
        width: '16px',
        height: '16px',
        border: '2px solid transparent',
        borderBottomColor: '#ffffff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
};
export function AIProviders() {
    const { settings, updateSettings, testProvider, loading, error } = useSettings();
    const { t } = useTranslation();
    // UI State
    const [showPrimaryKey, setShowPrimaryKey] = useState(false);
    const [showSecondaryKey, setShowSecondaryKey] = useState(false);
    const [enableFallback, setEnableFallback] = useState(settings?.aiProvider?.enableFallback ?? false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    // Provider state
    const [primaryProvider, setPrimaryProvider] = useState(settings?.aiProvider?.provider || 'gemini');
    const [secondaryProvider, setSecondaryProvider] = useState(settings?.aiProvider?.fallbackProvider && settings?.aiProvider?.fallbackProvider !== settings?.aiProvider?.provider
        ? settings.aiProvider.fallbackProvider
        : (settings?.aiProvider?.provider === 'gemini' ? 'openai' : 'gemini'));
    // API Keys
    const [geminiKey, setGeminiKey] = useState('');
    const [openaiKey, setOpenaiKey] = useState('');
    const [claudeKey, setClaudeKey] = useState('');
    // Models
    const [geminiModel, setGeminiModel] = useState('gemini-2.5-flash');
    const [openaiModel, setOpenaiModel] = useState('gpt-4.1-mini');
    const [claudeModel, setClaudeModel] = useState('claude-3-opus-20240229');
    // Advanced settings
    const [temperature, setTemperature] = useState(0.7);
    const [maxTokens, setMaxTokens] = useState(2048);
    const [enableStreaming, setEnableStreaming] = useState(settings?.aiProvider?.enableStreaming ?? true);
    const [thinkingEnabled, setThinkingEnabled] = useState(settings?.aiProvider?.thinkingEnabled ?? false);
    const [thinkingBudget, setThinkingBudget] = useState(settings?.aiProvider?.thinkingBudget ?? 512);
    // Test state
    const [testingProvider, setTestingProvider] = useState(null);
    const [testResult, setTestResult] = useState(null);
    // LLM Rules state
    const [llmRules, setLlmRules] = useState(settings?.aiProvider?.llmRules || []);
    const [dragIndex, setDragIndex] = useState(null);
    // Save state
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    // Load settings on mount
    useEffect(() => {
        if (settings?.aiProvider) {
            const ai = settings.aiProvider;
            // Load provider selection
            setPrimaryProvider(ai.provider);
            if (ai.fallbackProvider && ai.fallbackProvider !== ai.provider) {
                setSecondaryProvider(ai.fallbackProvider);
            }
            setEnableFallback(Boolean(ai.enableFallback));
            // Load API keys
            setGeminiKey(ai.geminiApiKey || '');
            setOpenaiKey(ai.openaiApiKey || '');
            setClaudeKey(ai.claudeApiKey || '');
            // Load models
            setGeminiModel(ai.geminiModel || 'gemini-2.5-flash');
            setOpenaiModel(ai.openaiModel || 'gpt-4.1-mini');
            setClaudeModel(ai.claudeModel || 'claude-3-opus-20240229');
            // Load advanced settings
            setTemperature(ai.temperature || 0.7);
            setMaxTokens(ai.maxTokens || 2048);
            setEnableStreaming(ai.enableStreaming ?? true);
            setThinkingEnabled(ai.thinkingEnabled ?? false);
            setThinkingBudget(ai.thinkingBudget ?? 512);
            // Load LLM rules
            if (ai.llmRules) {
                setLlmRules(ai.llmRules);
            }
        }
    }, [settings]);
    // Ensure fallback provider differs from primary
    useEffect(() => {
        if (primaryProvider === secondaryProvider) {
            const alternative = ['gemini', 'openai', 'claude'].find((provider) => provider !== primaryProvider);
            if (alternative) {
                setSecondaryProvider(alternative);
            }
        }
    }, [primaryProvider, secondaryProvider]);
    // Get current provider's API key and model
    const getCurrentProviderKey = () => {
        switch (primaryProvider) {
            case 'gemini': return geminiKey;
            case 'openai': return openaiKey;
            case 'claude': return claudeKey;
            default: return '';
        }
    };
    const getCurrentProviderModel = () => {
        switch (primaryProvider) {
            case 'gemini': return geminiModel;
            case 'openai': return openaiModel;
            case 'claude': return claudeModel;
            default: return '';
        }
    };
    const getProviderModels = (provider) => {
        return AI_MODELS[provider] || [];
    };
    // Save all settings
    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            setSaveSuccess(false);
            setTestResult(null);
            await updateSettings({
                aiProvider: {
                    provider: primaryProvider,
                    geminiApiKey: geminiKey,
                    openaiApiKey: openaiKey,
                    claudeApiKey: claudeKey,
                    geminiModel: geminiModel,
                    openaiModel: openaiModel,
                    claudeModel: claudeModel,
                    temperature: temperature,
                    maxTokens: maxTokens,
                    enableFallback,
                    fallbackProvider: enableFallback ? secondaryProvider : undefined,
                    enableStreaming,
                    llmRules,
                    thinkingEnabled,
                    thinkingBudget,
                }
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
        catch (err) {
            console.error('Failed to save settings:', err);
        }
        finally {
            setSaving(false);
        }
    };
    // Test provider connection
    const handleTestProvider = async (provider) => {
        setTestingProvider(provider);
        setTestResult(null);
        try {
            // First save the current API key
            const keyField = `${provider}ApiKey`;
            const apiKey = provider === 'gemini' ? geminiKey :
                provider === 'openai' ? openaiKey : claudeKey;
            if (settings?.aiProvider) {
                await updateSettings({
                    aiProvider: {
                        ...settings.aiProvider,
                        [keyField]: apiKey,
                    }
                });
            }
            // Then test the connection
            const result = await testProvider(provider);
            setTestResult({ provider, ...result });
        }
        catch (err) {
            setTestResult({
                provider,
                success: false,
                message: 'Test failed: ' + err.message
            });
        }
        finally {
            setTestingProvider(null);
        }
    };
    if (loading) {
        return (_jsx("div", { style: styles.loading, children: _jsx("div", { style: styles.loadingContainer, children: _jsxs("div", { style: styles.loadingContent, children: [_jsx("div", { style: styles.spinner }), _jsx("p", { style: { color: '#6b7280' }, children: t('loadingSettings') })] }) }) }));
    }
    return (_jsxs("div", { style: styles.page, children: [_jsxs("div", { style: styles.header, children: [_jsx("h1", { style: styles.title, children: t('aiProviders') }), _jsxs("p", { style: styles.subtitle, children: [t('aiProvidersDescription'), ' ', _jsxs("a", { href: `${window.wooAiChatbot?.documentationUrl || ''}/documentation/ai-providers`, target: "_blank", rel: "noopener noreferrer", style: styles.link, children: [t('learnMoreProviders'), " \u2192"] })] })] }), error && (_jsxs("div", { style: { ...styles.alert, ...styles.alertError }, children: [_jsx(AlertCircle, { style: { ...styles.alertIcon, color: '#dc2626' } }), _jsxs("div", { style: styles.alertContent, children: [_jsx("p", { style: { ...styles.alertTitle, color: '#991b1b' }, children: t('error') }), _jsx("p", { style: { ...styles.alertMessage, color: '#b91c1c' }, children: error.message })] })] })), saveSuccess && (_jsxs("div", { style: { ...styles.alert, ...styles.alertSuccess }, children: [_jsx(CheckCircle, { style: { ...styles.alertIcon, color: '#16a34a' } }), _jsxs("div", { style: styles.alertContent, children: [_jsx("p", { style: { ...styles.alertTitle, color: '#166534' }, children: t('settingsSaved') }), _jsx("p", { style: { ...styles.alertMessage, color: '#15803d' }, children: t('aiProviderSettingsSavedMessage') })] })] })), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: t('primaryProvider') }), _jsxs("div", { style: styles.fieldGroup, children: [_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('provider') }), _jsxs("select", { style: styles.select, value: primaryProvider, onChange: (e) => setPrimaryProvider(e.target.value), children: [_jsx("option", { value: "gemini", children: "Google Gemini" }), _jsx("option", { value: "openai", children: "OpenAI" }), _jsx("option", { value: "claude", children: "Anthropic Claude" })] })] }), _jsxs("div", { style: styles.field, children: [_jsxs("label", { style: styles.label, children: [t('apiKey'), " *"] }), _jsxs("div", { style: styles.inputRow, children: [_jsxs("div", { style: styles.inputWrapper, children: [_jsx("input", { type: showPrimaryKey ? 'text' : 'password', value: getCurrentProviderKey(), onChange: (e) => {
                                                            const value = e.target.value;
                                                            if (primaryProvider === 'gemini')
                                                                setGeminiKey(value);
                                                            else if (primaryProvider === 'openai')
                                                                setOpenaiKey(value);
                                                            else if (primaryProvider === 'claude')
                                                                setClaudeKey(value);
                                                        }, placeholder: "sk_test_\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", style: styles.inputWithButton }), _jsx("button", { onClick: () => setShowPrimaryKey(!showPrimaryKey), style: styles.eyeButton, type: "button", children: showPrimaryKey ? _jsx(EyeOff, { style: { width: '16px', height: '16px', color: '#6b7280' } }) : _jsx(Eye, { style: { width: '16px', height: '16px', color: '#6b7280' } }) })] }), _jsxs("button", { style: testingProvider === primaryProvider || !getCurrentProviderKey() ? styles.testButtonDisabled : styles.testButton, onClick: () => handleTestProvider(primaryProvider), disabled: testingProvider === primaryProvider || !getCurrentProviderKey(), children: [_jsx(Play, { style: { width: '16px', height: '16px' } }), testingProvider === primaryProvider ? t('testing') : t('test')] })] }), _jsxs("p", { style: styles.hint, children: [primaryProvider === 'gemini' && t('getGeminiApiKey'), primaryProvider === 'openai' && t('getOpenaiApiKey'), primaryProvider === 'claude' && t('getClaudeApiKey')] }), testResult?.provider === primaryProvider && (_jsxs("div", { style: { ...styles.testResult, color: testResult.success ? '#16a34a' : '#dc2626' }, children: [testResult.success ? _jsx(CheckCircle, { style: { width: '16px', height: '16px' } }) : _jsx(AlertCircle, { style: { width: '16px', height: '16px' } }), testResult.message] }))] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('model') }), _jsx("select", { style: styles.select, value: getCurrentProviderModel(), onChange: (e) => {
                                            const value = e.target.value;
                                            if (primaryProvider === 'gemini')
                                                setGeminiModel(value);
                                            else if (primaryProvider === 'openai')
                                                setOpenaiModel(value);
                                            else if (primaryProvider === 'claude')
                                                setClaudeModel(value);
                                        }, children: getProviderModels(primaryProvider).map(model => (_jsx("option", { value: model, children: MODEL_LABELS[model] ?? model }, model))) })] }), _jsxs("div", { style: styles.checkboxRow, children: [_jsx("input", { type: "checkbox", id: "fallback", checked: enableFallback, onChange: (e) => setEnableFallback(e.target.checked), style: styles.checkbox }), _jsx("label", { htmlFor: "fallback", style: styles.checkboxLabel, children: t('enableFallback') })] }), _jsxs("div", { style: styles.checkboxRow, children: [_jsx("input", { type: "checkbox", id: "show-advanced", checked: showAdvanced, onChange: (e) => setShowAdvanced(e.target.checked), style: styles.checkbox }), _jsx("label", { htmlFor: "show-advanced", style: styles.checkboxLabel, children: t('showAdvancedSettings') })] })] })] }), enableFallback && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: t('secondaryProviderFallback') }), _jsxs("div", { style: styles.fieldGroup, children: [_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('provider') }), _jsx("select", { style: styles.select, value: secondaryProvider, onChange: (e) => setSecondaryProvider(e.target.value), children: ['gemini', 'openai', 'claude']
                                            .filter((provider) => provider !== primaryProvider)
                                            .map((provider) => (_jsxs("option", { value: provider, children: [provider === 'gemini' && 'Google Gemini', provider === 'openai' && 'OpenAI', provider === 'claude' && 'Anthropic Claude'] }, provider))) })] }), _jsxs("div", { style: styles.field, children: [_jsxs("label", { style: styles.label, children: [t('apiKey'), " *"] }), _jsxs("div", { style: styles.inputRow, children: [_jsxs("div", { style: styles.inputWrapper, children: [_jsx("input", { type: showSecondaryKey ? 'text' : 'password', value: secondaryProvider === 'gemini' ? geminiKey :
                                                            secondaryProvider === 'openai' ? openaiKey : claudeKey, onChange: (e) => {
                                                            const value = e.target.value;
                                                            if (secondaryProvider === 'gemini')
                                                                setGeminiKey(value);
                                                            else if (secondaryProvider === 'openai')
                                                                setOpenaiKey(value);
                                                            else if (secondaryProvider === 'claude')
                                                                setClaudeKey(value);
                                                        }, placeholder: t('enterApiKey'), style: styles.inputWithButton }), _jsx("button", { onClick: () => setShowSecondaryKey(!showSecondaryKey), style: styles.eyeButton, type: "button", children: showSecondaryKey ? _jsx(EyeOff, { style: { width: '16px', height: '16px', color: '#6b7280' } }) : _jsx(Eye, { style: { width: '16px', height: '16px', color: '#6b7280' } }) })] }), _jsxs("button", { style: testingProvider === secondaryProvider || !(secondaryProvider === 'gemini' ? geminiKey :
                                                    secondaryProvider === 'openai' ? openaiKey : claudeKey) ? styles.testButtonDisabled : styles.testButton, onClick: () => handleTestProvider(secondaryProvider), disabled: testingProvider === secondaryProvider || !(secondaryProvider === 'gemini' ? geminiKey :
                                                    secondaryProvider === 'openai' ? openaiKey : claudeKey), children: [_jsx(Play, { style: { width: '16px', height: '16px' } }), testingProvider === secondaryProvider ? t('testing') : t('test')] })] }), testResult?.provider === secondaryProvider && (_jsxs("div", { style: { ...styles.testResult, color: testResult.success ? '#16a34a' : '#dc2626' }, children: [testResult.success ? _jsx(CheckCircle, { style: { width: '16px', height: '16px' } }) : _jsx(AlertCircle, { style: { width: '16px', height: '16px' } }), testResult.message] }))] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('model') }), _jsx("select", { style: styles.select, value: secondaryProvider === 'gemini' ? geminiModel :
                                            secondaryProvider === 'openai' ? openaiModel : claudeModel, onChange: (e) => {
                                            const value = e.target.value;
                                            if (secondaryProvider === 'gemini')
                                                setGeminiModel(value);
                                            else if (secondaryProvider === 'openai')
                                                setOpenaiModel(value);
                                            else if (secondaryProvider === 'claude')
                                                setClaudeModel(value);
                                        }, children: getProviderModels(secondaryProvider).map(model => (_jsx("option", { value: model, children: MODEL_LABELS[model] ?? model }, model))) })] })] })] })), showAdvanced && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: t('advancedSettings') }), _jsxs("div", { style: styles.fieldGroup, children: [_jsxs("div", { style: styles.sliderContainer, children: [_jsxs("div", { style: styles.sliderHeader, children: [_jsx("label", { style: styles.label, children: t('temperature') }), _jsx("span", { style: styles.sliderValue, children: temperature.toFixed(1) })] }), _jsx("input", { type: "range", min: "0", max: "1", step: "0.1", value: temperature, onChange: (e) => setTemperature(parseFloat(e.target.value)), style: styles.slider }), _jsx("p", { style: styles.hint, children: t('temperatureHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('maxTokens') }), _jsx("input", { type: "number", value: maxTokens, onChange: (e) => setMaxTokens(parseInt(e.target.value) || 0), style: styles.input }), _jsx("p", { style: styles.hint, children: t('maxTokensHint') })] }), _jsxs("div", { style: styles.checkboxRow, children: [_jsx("input", { type: "checkbox", id: "streaming", checked: enableStreaming, onChange: (e) => setEnableStreaming(e.target.checked), style: styles.checkbox }), _jsx("label", { htmlFor: "streaming", style: styles.checkboxLabel, children: t('enableStreaming') })] }), _jsxs("div", { style: { ...styles.field, borderTop: '1px solid #f3f4f6', paddingTop: '16px' }, children: [_jsxs("div", { style: styles.checkboxRow, children: [_jsx("input", { type: "checkbox", id: "thinking-enabled", checked: thinkingEnabled, onChange: (e) => setThinkingEnabled(e.target.checked), style: styles.checkbox }), _jsx("label", { htmlFor: "thinking-enabled", style: styles.checkboxLabel, children: "Enable AI Thinking" })] }), _jsx("p", { style: styles.hint, children: "When enabled, the AI model \u201Cthinks\u201D before responding. Improves tool selection quality but adds 1-3 seconds latency and increases cost. Applies to Gemini 2.5+ and 3.x models only." })] }), thinkingEnabled && (_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Thinking Budget (tokens)" }), _jsx("input", { type: "number", value: thinkingBudget, min: 0, max: 8192, onChange: (e) => {
                                            const val = parseInt(e.target.value) || 0;
                                            setThinkingBudget(Math.min(8192, Math.max(0, val)));
                                        }, style: { ...styles.input, maxWidth: '200px' } }), _jsx("p", { style: styles.hint, children: "Number of tokens reserved for thinking. Default: 512. Range: 0\u20138192." })] }))] })] })), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }, children: [_jsxs("div", { children: [_jsx("h2", { style: { fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }, children: "Custom Rules" }), _jsx("p", { style: { fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }, children: "Add rules to control AI behavior. Rules have the highest priority and override the system prompt. Drag to reorder \u2014 higher rules have higher priority." })] }), _jsxs("button", { onClick: () => {
                                    setLlmRules([...llmRules, { id: crypto.randomUUID?.() || `rule-${Date.now()}`, text: '', enabled: true }]);
                                }, style: {
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '8px 16px', borderRadius: '8px', border: '1px solid #6366f1',
                                    background: '#f5f3ff', color: '#6366f1', fontSize: '13px', fontWeight: 500,
                                    cursor: 'pointer', whiteSpace: 'nowrap'
                                }, children: [_jsx(Plus, { style: { width: '14px', height: '14px' } }), "Add Rule"] })] }), llmRules.length === 0 && (_jsx("div", { style: {
                            padding: '32px', textAlign: 'center', borderRadius: '8px',
                            border: '2px dashed #e5e7eb', color: '#9ca3af', fontSize: '14px'
                        }, children: "No rules yet. Click \"Add Rule\" to create your first custom instruction." })), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: llmRules.map((rule, index) => (_jsxs("div", { draggable: true, onDragStart: () => setDragIndex(index), onDragOver: (e) => { e.preventDefault(); }, onDrop: () => {
                                if (dragIndex !== null && dragIndex !== index) {
                                    const updated = [...llmRules];
                                    const [moved] = updated.splice(dragIndex, 1);
                                    updated.splice(index, 0, moved);
                                    setLlmRules(updated);
                                }
                                setDragIndex(null);
                            }, onDragEnd: () => setDragIndex(null), style: {
                                display: 'flex', alignItems: 'flex-start', gap: '8px',
                                padding: '12px', borderRadius: '10px',
                                border: dragIndex === index ? '2px solid #6366f1' : '1px solid #e5e7eb',
                                background: !rule.enabled ? '#f9fafb' : '#ffffff',
                                opacity: dragIndex === index ? 0.5 : 1,
                                cursor: 'grab',
                                transition: 'border-color 0.15s, opacity 0.15s'
                            }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '4px', paddingTop: '8px', flexShrink: 0 }, children: [_jsx(GripVertical, { style: { width: '16px', height: '16px', color: '#9ca3af' } }), _jsxs("span", { style: {
                                                fontSize: '11px', fontWeight: 700, color: '#6366f1',
                                                background: '#f0f0ff', borderRadius: '4px', padding: '2px 6px',
                                                minWidth: '20px', textAlign: 'center'
                                            }, children: ["#", index + 1] })] }), _jsx("textarea", { value: rule.text, onChange: (e) => {
                                        const updated = [...llmRules];
                                        updated[index] = { ...updated[index], text: e.target.value };
                                        setLlmRules(updated);
                                    }, placeholder: "e.g. Always recommend our premium mattress collection first", rows: 2, style: {
                                        flex: 1, padding: '8px 12px', borderRadius: '8px',
                                        border: '1px solid #d1d5db', fontSize: '13px', lineHeight: '1.5',
                                        resize: 'vertical', fontFamily: 'inherit',
                                        color: rule.enabled ? '#111827' : '#9ca3af'
                                    } }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px', flexShrink: 0 }, children: [_jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }, children: [_jsx("input", { type: "checkbox", checked: rule.enabled, onChange: (e) => {
                                                        const updated = [...llmRules];
                                                        updated[index] = { ...updated[index], enabled: e.target.checked };
                                                        setLlmRules(updated);
                                                    }, style: { accentColor: '#6366f1' } }), "On"] }), _jsx("button", { onClick: () => setLlmRules(llmRules.filter((_, i) => i !== index)), style: {
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                width: '28px', height: '28px', borderRadius: '6px',
                                                border: '1px solid #fecaca', background: '#fef2f2',
                                                cursor: 'pointer', color: '#ef4444'
                                            }, title: "Delete rule", children: _jsx(Trash2, { style: { width: '14px', height: '14px' } }) })] })] }, rule.id))) })] }), _jsxs("div", { style: styles.footer, children: [_jsx("button", { style: styles.cancelButton, onClick: () => window.location.reload(), disabled: saving, children: t('cancel') }), _jsx("button", { style: saving ? styles.saveButtonDisabled : styles.saveButton, onClick: handleSaveSettings, disabled: saving, children: saving ? (_jsxs(_Fragment, { children: [_jsx("div", { style: styles.smallSpinner }), t('saving')] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { style: { width: '16px', height: '16px' } }), t('saveChanges')] })) })] })] }));
}
